import { Cloud, KeyRound, ShieldCheck } from 'lucide-react';
import { SignInButton, UserProfile, useAuth } from '@clerk/react';
import { Link } from 'react-router-dom';

import { ACCOUNT_SYNC_ENABLED, AccountDataSyncCard } from '@/features/account';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export function AccountPage() {
  if (ACCOUNT_SYNC_ENABLED) {
    return <ConfiguredAccountPage />;
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">Bunko account</h1>
        <p className="text-sm text-muted-foreground">
          Account sync will use Clerk for sign-in and Cloudflare for encrypted profile storage.
        </p>
      </header>

      <Card className={ACCOUNT_SYNC_ENABLED ? undefined : 'border-amber-500/40 bg-amber-500/5'}>
        <CardHeader>
          <CardTitle>Account sync not configured</CardTitle>
          <CardDescription>
            Set VITE_CLERK_PUBLISHABLE_KEY and configure the Worker account database to enable
            sign-in.
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="space-y-2">
            <ShieldCheck className="h-5 w-5 text-emerald-500" />
            <CardTitle className="text-base">Identity</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Clerk proves which Bunko account owns the encrypted sync profile.
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="space-y-2">
            <KeyRound className="h-5 w-5 text-blue-500" />
            <CardTitle className="text-base">Encryption</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            A random sync key encrypts settings, history, and source credentials before upload.
            Bunko backend infrastructure can technically recover this account key.
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="space-y-2">
            <Cloud className="h-5 w-5 text-purple-500" />
            <CardTitle className="text-base">Signed-in devices</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            Signing into the same Bunko account is enough to retrieve the account sync key and
            restore a backup on a fresh device.
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ConfiguredAccountPage() {
  const { isLoaded, isSignedIn } = useAuth();
  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">Bunko account</h1>
        <p className="text-sm text-muted-foreground">
          Sign in to back up and restore Bunko data across signed-in devices.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>{isSignedIn ? 'Signed in' : 'Sign in'}</CardTitle>
          <CardDescription>
            {isSignedIn
              ? 'This browser can access your account sync key through your signed-in Clerk session.'
              : 'Clerk handles identity. Bunko encrypts account data for transit and storage, but this is not device-approved end-to-end sync.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!isLoaded ? (
            <p className="text-sm text-muted-foreground">Loading Clerk session…</p>
          ) : isSignedIn ? (
            <div className="flex flex-col gap-3 text-sm text-muted-foreground">
              <p>Sync controls live below. Clerk profile settings are separate.</p>
              <Button asChild variant="outline" className="w-fit">
                <Link to="/account/profile">Manage Clerk profile</Link>
              </Button>
            </div>
          ) : (
            <SignInButton mode="modal">
              <Button>Sign in with Clerk</Button>
            </SignInButton>
          )}
        </CardContent>
      </Card>

      {isLoaded && isSignedIn && <AccountDataSyncCard />}
    </div>
  );
}

export function AccountProfilePage() {
  if (!ACCOUNT_SYNC_ENABLED) {
    return <AccountPage />;
  }

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold">Account profile</h1>
        <p className="text-sm text-muted-foreground">
          Clerk manages sign-in details separately from Bunko sync data.
        </p>
      </header>
      <Card>
        <CardContent className="p-0 sm:p-6">
          <UserProfile routing="path" path="/account/profile" />
        </CardContent>
      </Card>
    </div>
  );
}
